/**
 * Tag Rules Engine V2 - Stage-based tag generation
 * Uses pipeline stages to determine tag rules
 */

import config from './tagRules.config.json';
import { ExpertiseCardProps } from '../ExpertiseCard.types';
import { parseDate as parseDateFromUtils } from '@/core/utils/date.utils';

// Type definitions
export interface Tag {
  text: string;
  className: string;
  priority: number;
}

export interface ExpertiseData {
  stageId?: number;
  date?: Date | string;
  date_du_rv?: Date | string;
  expert?: string;
  [key: string]: any;
}

/**
 * Date calculation utilities following the new rules:
 * - Reference timestamp starts from "next day"
 * - Before date: "Nouveau"
 * - On date: "Aujourd'hui"
 * - After date: Day counter starting at 1
 */
export class DateCalculator {
  /**
   * Parse date string handling various formats (ISO, MM/DD/YYYY legacy)
   * Uses centralized parseDate utility for consistency
   */
  static parseDate(date: Date | string | undefined): Date | null {
    if (!date) return null;

    // If already a Date object, use it
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }

    // Use centralized parseDate for string handling
    return parseDateFromUtils(date.toString());
  }
  
  /**
   * Get the reference date adjusted to start of next day
   */
  static getAdjustedReferenceDate(date: Date | string | undefined): Date | null {
    const parsedDate = DateCalculator.parseDate(date);
    if (!parsedDate) return null;
    
    // Set to start of next day (00:00:00 of the following day)
    const adjustedDate = new Date(parsedDate);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    adjustedDate.setHours(0, 0, 0, 0);
    
    return adjustedDate;
  }
  
  /**
   * Get today's date at start of day
   */
  static getTodayStart(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }
  
  /**
   * Calculate days since reference date with new rules
   */
  static calculateDaysSince(referenceDate: Date | string | undefined): { status: 'nouveau' | 'aujourdhui' | 'days', days?: number } {
    const adjustedRef = DateCalculator.getAdjustedReferenceDate(referenceDate);
    if (!adjustedRef) return { status: 'nouveau' };
    
    const today = DateCalculator.getTodayStart();
    const diffTime = today.getTime() - adjustedRef.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      // Current date is before the adjusted reference date
      return { status: 'nouveau' };
    } else if (diffDays === 0) {
      // Current date equals the adjusted reference date
      return { status: 'aujourdhui' };
    } else {
      // Current date is after the adjusted reference date
      return { status: 'days', days: diffDays };
    }
  }
  
  /**
   * Calculate days until appointment (for stage 40)
   */
  static calculateDaysUntilAppointment(appointmentDate: Date | string | undefined): { status: 'past' | 'aujourdhui' | 'future', days?: number } {
    const parsedDate = DateCalculator.parseDate(appointmentDate);
    if (!parsedDate) return { status: 'past' };
    
    // Set appointment date to start of day
    const apptDate = new Date(parsedDate);
    apptDate.setHours(0, 0, 0, 0);
    
    const today = DateCalculator.getTodayStart();
    const diffTime = apptDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'past', days: Math.abs(diffDays) };
    } else if (diffDays === 0) {
      return { status: 'aujourdhui' };
    } else {
      return { status: 'future', days: diffDays };
    }
  }
  
  /**
   * Format date for display
   */
  static formatDate(date: Date | string | undefined): string {
    const parsedDate = DateCalculator.parseDate(date);
    if (!parsedDate) return 'N/A';
    
    return parsedDate.toLocaleDateString(config.display.dateFormat, config.display.dateOptions as any);
  }
}

/**
 * Stage-based tag generation
 */
export class StageTagGenerator {
  /**
   * Get the stage rule configuration
   */
  private static getStageRule(stageId: number | undefined) {
    if (!stageId) return null;
    return (config.stageRules as Record<string, any>)[stageId.toString()] || null;
  }
  
  /**
   * Generate tags for a specific stage
   */
  static generateStageTags(data: ExpertiseData): Tag[] {
    const tags: Tag[] = [];
    const stageRule = StageTagGenerator.getStageRule(data.stageId);
    
    if (!stageRule) {
      // No stage rule found, return basic tags
      return StageTagGenerator.generateDefaultTags(data);
    }
    
    // Handle special rules (e.g., stage 40)
    if (stageRule.specialRules) {
      const specialTags = StageTagGenerator.handleSpecialRules(stageRule, data);
      tags.push(...specialTags);
    }
    
    // Get the reference date field
    const referenceDate = data[stageRule.referenceField];
    
    // Calculate day status
    if (stageRule.showDaysSince && referenceDate) {
      const dayCalc = DateCalculator.calculateDaysSince(referenceDate);
      
      switch (dayCalc.status) {
        case 'nouveau':
          tags.push({
            text: config.dateCalculation.labels.beforeDate,
            className: config.tagTypes.nouveau.className,
            priority: config.tagTypes.nouveau.priority
          });
          break;
          
        case 'aujourdhui':
          tags.push({
            text: config.dateCalculation.labels.onDate,
            className: config.tagTypes.aujourdhui.className,
            priority: config.tagTypes.aujourdhui.priority
          });
          break;
          
        case 'days':
          if (dayCalc.days !== undefined) {
            // Add days since tag
            tags.push({
              text: config.dateCalculation.labels.daysSinceFormat.replace('{days}', dayCalc.days.toString()),
              className: config.tagTypes.daysSince.className,
              priority: config.tagTypes.daysSince.priority
            });
            
            // Check if urgent based on days
            if (stageRule.urgentAfterDays && !stageRule.neverUrgent && dayCalc.days > stageRule.urgentAfterDays) {
              tags.push({
                text: config.tagTypes.urgent.text,
                className: config.tagTypes.urgent.className,
                priority: config.tagTypes.urgent.priority
              });
            }
          }
          break;
      }
    }
    
    return tags;
  }
  
  /**
   * Handle special stage rules (e.g., stage 40 appointment rules)
   */
  private static handleSpecialRules(stageRule: any, data: ExpertiseData): Tag[] {
    const tags: Tag[] = [];
    
    if (stageRule.specialRules.type === 'appointment') {
      // Check if expert is assigned
      if (stageRule.specialRules.checkExpertAssigned && !data.expert) {
        tags.push({
          text: stageRule.specialRules.messages.noExpert,
          className: config.tagTypes.warning.className,
          priority: config.tagTypes.warning.priority
        });
      }
      
      // Check if appointment is set
      if (stageRule.specialRules.checkAppointmentSet && !data.date_du_rv) {
        tags.push({
          text: stageRule.specialRules.messages.noAppointment,
          className: config.tagTypes.warning.className,
          priority: config.tagTypes.warning.priority
        });
      } else if (data.date_du_rv) {
        // Show days until appointment
        const apptCalc = DateCalculator.calculateDaysUntilAppointment(data.date_du_rv);
        
        switch (apptCalc.status) {
          case 'aujourdhui':
            tags.push({
              text: config.dateCalculation.labels.onDate,
              className: config.tagTypes.aujourdhui.className,
              priority: config.tagTypes.aujourdhui.priority
            });
            break;
            
          case 'future':
            if (apptCalc.days) {
              tags.push({
                text: config.tagTypes.daysUntil.textFormat.replace('{days}', apptCalc.days.toString()),
                className: config.tagTypes.daysUntil.className,
                priority: config.tagTypes.daysUntil.priority
              });
            }
            break;
            
          case 'past':
            if (apptCalc.days) {
              tags.push({
                text: `Dépassé ${apptCalc.days}j`,
                className: config.tagTypes.urgent.className,
                priority: config.tagTypes.urgent.priority
              });
            }
            break;
        }
      }
    }
    
    return tags;
  }
  
  /**
   * Generate default tags when no stage is specified
   */
  private static generateDefaultTags(data: ExpertiseData): Tag[] {
    const tags: Tag[] = [];
    
    // Use date field as default reference
    const dayCalc = DateCalculator.calculateDaysSince(data.date);
    
    switch (dayCalc.status) {
      case 'nouveau':
        tags.push({
          text: config.dateCalculation.labels.beforeDate,
          className: config.tagTypes.nouveau.className,
          priority: config.tagTypes.nouveau.priority
        });
        break;
        
      case 'aujourdhui':
        tags.push({
          text: config.dateCalculation.labels.onDate,
          className: config.tagTypes.aujourdhui.className,
          priority: config.tagTypes.aujourdhui.priority
        });
        break;
        
      case 'days':
        if (dayCalc.days !== undefined) {
          tags.push({
            text: config.dateCalculation.labels.daysSinceFormat.replace('{days}', dayCalc.days.toString()),
            className: config.tagTypes.daysSince.className,
            priority: config.tagTypes.daysSince.priority
          });
        }
        break;
    }
    
    return tags;
  }
}

/**
 * Main tag generation function
 */
export function generateTags(props: Partial<ExpertiseCardProps>): string[] {
  const data: ExpertiseData = {
    stageId: props.stageId,
    date: props.createdAt,
    date_du_rv: props.dueDate,
    expert: props.expertName,
    ...props
  };
  
  // Generate stage-based tags
  const tags = StageTagGenerator.generateStageTags(data);
  
  // Sort by priority and limit to max tags
  return tags
    .sort((a, b) => a.priority - b.priority)
    .slice(0, config.display.maxTags)
    .map(tag => tag.text);
}

/**
 * Get CSS class for a tag based on its text
 */
export function getTagClass(tagText: string): string {
  const text = tagText.toLowerCase();
  
  // Check for specific patterns
  if (text === 'urgent') {
    return config.tagTypes.urgent.className;
  }
  
  if (text === 'nouveau') {
    return config.tagTypes.nouveau.className;
  }
  
  if (text === 'aujourd\'hui') {
    return config.tagTypes.aujourdhui.className;
  }
  
  // Check for days pattern (e.g., "5j", "J-3")
  if (/^\d+j$/.test(text)) {
    return config.tagTypes.daysSince.className;
  }
  
  if (/^j-\d+$/i.test(text)) {
    return config.tagTypes.daysUntil.className;
  }
  
  // Check for warning messages
  if (text.includes('non assigné') || text.includes('pas de')) {
    return config.tagTypes.warning.className;
  }
  
  if (text.includes('dépassé')) {
    return config.tagTypes.urgent.className;
  }
  
  return config.tagTypes.info.className;
}

/**
 * Calculate progress percentage based on stage
 */
export function calculateProgress(
  status?: string,
  stageId?: number,
  customProgress?: number
): number {
  // Use custom progress if provided
  if (customProgress !== undefined && customProgress >= 0 && customProgress <= 100) {
    return customProgress;
  }
  
  // Calculate progress based on stage ID position in the pipeline
  if (stageId !== undefined && stageId !== null) {
    // Stage IDs: 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110
    // Map to progress: 9%, 18%, 27%, 36%, 45%, 54%, 64%, 73%, 82%, 91%, 100%
    const stageProgressMap: Record<number, number> = {
      10: 9,   // Bureau à encoder
      20: 18,  // Bureau à planifier
      30: 27,  // RV en attente
      40: 36,  // Expert à visiter
      50: 45,  // Expert à achever
      60: 54,  // Bureau à valoriser
      70: 64,  // Bureau à achever
      80: 73,  // Attente doc/infos
      90: 82,  // Attente paiement
      100: 91, // Attente confirmation
      110: 100 // À transmettre
    };
    
    const progress = stageProgressMap[stageId];
    if (progress !== undefined) {
      return progress;
    } else {
      // Unknown stageId, defaulting to 9%
    }
  } else {
    // No stageId provided for status, defaulting to 9%
  }
  
  // Default to first stage
  return 9;
}

/**
 * Determine if an expertise should be marked as urgent
 */
export function calculateUrgency(props: Partial<ExpertiseCardProps>): boolean {
  const data: ExpertiseData = {
    stageId: props.stageId,
    date: props.createdAt,
    date_du_rv: props.dueDate,
    expert: props.expertName,
    ...props
  };
  
  const tags = StageTagGenerator.generateStageTags(data);
  return tags.some(tag => tag.className === config.tagTypes.urgent.className);
}

/**
 * Get status color - extended to handle backend status values
 */
export function getStatusColor(status: string): string {
  const statusLower = status.toLowerCase();

  // Direct matches for enum values
  const directStatusColors: Record<string, string> = {
    nouveau: 'success',
    en_cours: 'info',
    termine: 'default',
    terminé: 'default',
    archive: 'muted',
    urgent: 'danger',
    attente_info: 'warning',
    validation: 'primary'
  };

  // Check direct match first
  if (directStatusColors[statusLower]) {
    return directStatusColors[statusLower];
  }

  // Pattern-based matching for backend status values

  // Completed/Finished statuses -> default/muted
  if (statusLower.includes('terminé') ||
      statusLower.includes('fini') ||
      statusLower.includes('cloturé') ||
      statusLower.includes('transmis')) {
    return 'default';
  }

  // Payment-related statuses -> warning
  if (statusLower.includes('paiement') ||
      statusLower.includes('rappel') ||
      statusLower.includes('factur') ||
      statusLower.includes('perception')) {
    return 'warning';
  }

  // Waiting/Pending statuses -> info
  if (statusLower.includes('attente') ||
      statusLower.includes('en cours') ||
      statusLower.includes('pending')) {
    return 'info';
  }

  // Urgent/Alert statuses -> danger
  if (statusLower.includes('urgent') ||
      statusLower.includes('retard') ||
      statusLower.includes('dépassé') ||
      statusLower.includes('annulé')) {
    return 'danger';
  }

  // Validation/Review statuses -> primary
  if (statusLower.includes('validation') ||
      statusLower.includes('révision') ||
      statusLower.includes('contrôle') ||
      statusLower.includes('vérif')) {
    return 'primary';
  }

  // New/Created statuses -> success
  if (statusLower.includes('nouveau') ||
      statusLower.includes('créé') ||
      statusLower.includes('new')) {
    return 'success';
  }

  // Default fallback
  return 'info';
}

// Export utilities
export const formatDate = DateCalculator.formatDate;
export const calculateTimeSince = (date: Date | string | undefined): string | null => {
  const calc = DateCalculator.calculateDaysSince(date);
  
  switch (calc.status) {
    case 'nouveau':
      return config.dateCalculation.labels.beforeDate;
    case 'aujourdhui':
      return config.dateCalculation.labels.onDate;
    case 'days':
      return calc.days ? `${calc.days}j` : null;
    default:
      return null;
  }
};

// Default export
export default {
  generateTags,
  getTagClass,
  calculateProgress,
  calculateUrgency,
  getStatusColor,
  formatDate,
  calculateTimeSince,
  DateCalculator,
  StageTagGenerator
};